import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { digitsOnly } from "views/inbox/utils/contactMethods";
import { phoneNumbersApi } from "views/phone-numbers/lib/api";
import type { PhoneNumber } from "views/phone-numbers/lib/types";

// Alphabetical by name, then by number among same-named lines; numbers without
// a name go last.
const byName = (a: PhoneNumber, b: PhoneNumber): number => {
  const nameA = a.name?.trim() ?? "";
  const nameB = b.name?.trim() ?? "";
  if (!nameA !== !nameB) return nameA ? -1 : 1;
  return (
    nameA.localeCompare(nameB, undefined, { sensitivity: "base" }) ||
    a.phone_number.localeCompare(b.phone_number)
  );
};

// AI Leasing Agent lines stay in, though a lead calling one back reaches the AI
// agent: a product decision on CRM-1659, same as the Inbox list.
const canCallBackFrom = (number: PhoneNumber): boolean =>
  number.can_voice && number.status === "active";

/**
 * The numbers a missed call can be returned from (CRM-1659): every active
 * voice number the user can see. Unlike the Inbox picker it is NOT scoped to
 * the lead's property: the list endpoint already limits it to the user's
 * properties and "All properties" numbers, and the server re-checks the pick.
 * The line the call came in on comes first; the rest are alphabetical.
 */
export const useCallBackNumbers = (lineNumber: string | null | undefined) => {
  const { data, isLoading } = useQuery({
    queryKey: ["VOICEABLE_PHONE_NUMBERS", null],
    queryFn: () => phoneNumbersApi.listAll()
  });

  const { numbers, propertyLineId } = useMemo(() => {
    const usable = (data?.items ?? []).filter(canCallBackFrom);
    const line = lineNumber ? digitsOnly(lineNumber) : null;
    const propertyLine = line
      ? usable.find((number) => digitsOnly(number.phone_number) === line)
      : undefined;
    const others = usable
      .filter((number) => number !== propertyLine)
      .sort(byName);

    return {
      numbers: propertyLine ? [propertyLine, ...others] : others,
      propertyLineId: propertyLine?.id
    };
  }, [data, lineNumber]);

  return { numbers, propertyLineId, isLoading };
};
